/**
 * ThemeContext - Global Theme State Management
 * 
 * Manages light/dark theme switching across the application.
 * Persists preference to localStorage and optionally syncs with backend.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const ThemeContext = createContext(null);

// Storage key for localStorage
const THEME_STORAGE_KEY = 'asset_track_theme';

export function ThemeProvider({ children }) {
  // Initialize from localStorage or default to dark
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    }
    return 'dark'; // Default theme
  });

  const [isLoading, setIsLoading] = useState(false);

  /**
   * Apply theme to document root
   */
  const applyTheme = useCallback((themeValue) => {
    const root = document.documentElement;
    
    if (themeValue === 'light') {
      root.classList.remove('dark');
      root.classList.add('light');
    } else {
      root.classList.remove('light');
      root.classList.add('dark');
    }

    // Also set data attribute for CSS selectors
    root.setAttribute('data-theme', themeValue);
  }, []);

  /**
   * Set theme and persist
   */
  const setTheme = useCallback(async (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') return;

    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    applyTheme(newTheme);

    // Try to sync with backend (optional - won't fail if not available)
    try {
      if (api.hasToken()) {
        await api.patch('/api/users/me/preferences', {
          themePreference: newTheme
        });
      }
    } catch (err) {
      // Silently fail - localStorage is the primary storage
      console.log('Theme preference sync skipped:', err.message);
    }
  }, [applyTheme]);

  /**
   * Toggle between light and dark
   */
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  /**
   * Load theme preference from backend on mount
   */
  useEffect(() => {
    const loadPreference = async () => {
      if (!api.hasToken()) return;

      try {
        setIsLoading(true);
        const prefs = await api.get('/api/users/me/preferences');
        if (prefs?.theme_preference) {
          const serverTheme = prefs.theme_preference;
          // Only update if different from current
          if (serverTheme !== theme) {
            setThemeState(serverTheme);
            localStorage.setItem(THEME_STORAGE_KEY, serverTheme);
            applyTheme(serverTheme);
          }
        }
      } catch (err) {
        // Use localStorage value on error
        console.log('Could not load theme preference from server');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreference();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Apply theme on initial render and when theme changes
   */
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isLoading,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to use theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;

